import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import MapHealth from '../components/MapHealth'
import AskSensify from './AskSensify'
import { HouseholdShareCard } from './HouseholdCard'

// ── Tier colors (matches the rest of the app) ──
const TIER = {
  Safe: { dot: '#2C9D8A', text: '#1A6256', bg: '#DEF2EE' },
  Limit: { dot: '#E8941F', text: '#8A5410', bg: '#FCEFD9' },
  Avoid: { dot: '#D64545', text: '#A32D2D', bg: '#FBE9E9' },
}

const SYMPTOM_OPTIONS = ['Bloating', 'Gas / cramping', 'Reflux', 'Fatigue', 'Headache', 'Skin issue', 'Brain fog', 'Other']
const SEVERITY = ['mild', 'moderate', 'severe']

// ── Locked preview for users still IN the protocol (months 1–6). ──
// Maintain unlocks at completion; this plants the seed without giving
// the product away or confusing mid-protocol users.
function MaintainPreview({ profile, session }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const optedIn = profile?.maintain_opt_in === true

  const startOptIn = async () => {
    setBusy(true); setErr('')
    try {
      const res = await fetch('/.netlify/functions/create-maintain-setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: session.user.id }),
      })
      const data = await res.json()
      if (data.url) { window.location.href = data.url; return }
      setErr(data.error || 'Could not start. Try again.')
    } catch (e) { setErr('Could not start. Try again.') }
    setBusy(false)
  }

  const cancelOptIn = async () => {
    setBusy(true)
    // Checked (QA sentinel S2): a silently failed cancel left the user looking
    // still-opted-in after the reload, with their card still on file.
    const { error, data } = await supabase.from('profiles').update({ maintain_opt_in: false }).eq('id', session.user.id).select()
    if (error || !data || data.length === 0) {
      setBusy(false)
      alert("That didn't go through — your reservation wasn't cancelled. Please try again.")
      return
    }
    window.location.reload()
  }

  const features = [
    { t: 'Ask Sensify, unlimited', d: 'Any food, menu, label, or photo, checked against your map.' },
    { t: 'Continuous logging', d: 'Meals and symptoms tracked, so the map stays honest.' },
    { t: 'Drift detection', d: 'Old symptoms creeping back get flagged before they take over.' },
    { t: 'Re-testing cycles', d: 'Bodies change. Re-run any food and update its verdict.' },
  ]

  return (
    <div style={{ maxWidth: '460px', margin: '0 auto', padding: '32px 22px' }}>
      <style>{`
        @keyframes mtRowIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes mtGlow { 0%, 100% { opacity: 0.05; } 50% { opacity: 0.11; } }
        @keyframes mtRise { from { opacity: 0; transform: translateY(18px) scale(0.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div style={{ marginBottom: '18px', animation: 'mtRowIn 0.5s ease both' }}>
        <div className="sn-micro" style={{ marginBottom: '8px' }}>After month six</div>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: '28px', fontWeight: 300, color: '#1C1C1C', lineHeight: 1.15 }}>The map is yours.<br /><em style={{ fontStyle: 'italic', color: '#3D5C3C' }}>Keeping it alive is Maintain.</em></div>
      </div>

      <div style={{ background: '#22301F', borderRadius: '24px', padding: '20px 22px', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-3)', animation: 'mtRise 0.7s cubic-bezier(0.16,1,0.3,1) both' }}>
        <div style={{ position: 'absolute', top: -70, right: -70, width: 240, height: 240, borderRadius: '50%', background: '#8BAE8A', animation: 'mtGlow 5s ease-in-out infinite', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -90, left: -60, width: 200, height: 200, borderRadius: '50%', background: '#E8941F', opacity: 0.05, pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 0', borderBottom: i < features.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', animation: `mtRowIn 0.55s ease ${0.4 + i * 0.15}s both` }}>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', color: '#8BAE8A', width: '20px' }}>{`0${i + 1}`}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#FAF8F4' }}>{f.t}</div>
                <div style={{ fontSize: '11.5px', color: 'rgba(250,248,244,0.6)', marginTop: '2px' }}>{f.d}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.08)', position: 'relative', animation: 'mtRowIn 0.55s ease 1.1s both' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
            <span style={{ fontFamily: 'Fraunces, serif', fontSize: '24px', fontStyle: 'normal', color: '#FAF8F4' }}>$12.99</span>
            <span style={{ fontFamily: 'DM Mono, monospace', fontSize: '9px', color: 'rgba(250,248,244,0.7)', letterSpacing: '0.5px' }}>/MO</span>
          </div>
          <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '8.5px', color: 'rgba(250,248,244,0.7)', letterSpacing: '0.5px' }}>CANCEL ANYTIME</div>
        </div>
      </div>

      {optedIn ? (
        <div style={{ marginTop: '14px', background: '#EDF3ED', borderRadius: '16px', padding: '15px 17px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#3D5C3C', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', flexShrink: 0 }}>\u2713</span>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#3D5C3C' }}>You're set for Maintain.</span>
          </div>
          <div style={{ fontSize: '12px', color: '#5A6E58', lineHeight: 1.55 }}>Your card is saved but not charged. Maintain begins automatically when your protocol completes.</div>
          <button onClick={cancelOptIn} disabled={busy} style={{ background: 'none', border: 'none', color: '#8A8A82', fontSize: '11.5px', textDecoration: 'underline', cursor: 'pointer', padding: 0, marginTop: '8px' }}>Cancel my reservation</button>
        </div>
      ) : (
        <div style={{ marginTop: '14px' }}>
          <button onClick={startOptIn} disabled={busy} className="sn-btn" style={{ width: '100%', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Opening secure checkout\u2026' : 'Reserve Maintain, $0 today'}
          </button>
          <div style={{ fontSize: '11px', color: '#A0A096', textAlign: 'center', marginTop: '8px', lineHeight: 1.55 }}>Saves your card securely with Stripe. Nothing is charged until your protocol completes, and you can cancel anytime before then.</div>
          {err && <div style={{ fontSize: '12px', color: '#D64545', textAlign: 'center', marginTop: '6px' }}>{err}</div>}
        </div>
      )}

      {/* MAP HEALTH — the drift-detection promise, shown live as a preview
          of what Maintain's monthly report looks like on the user's own data */}
      <div style={{ marginTop: 18 }}>
        <div className="sn-micro sn-micro-dim" style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>What drift detection looks like</div>
        <MapHealth session={session} profile={profile} />
      </div>

      <div style={{ fontSize: '11.5px', color: '#A0A096', textAlign: 'center', marginTop: '16px', lineHeight: 1.6 }}>Your completed Food Map is yours forever, subscription or not.</div>
    </div>
  )
}

export default function MaintainHub({ session, profile, isAdmin }) {
  // Wrapper keeps hooks rules clean: the gate never sits above hooks.
  // Admin preview: lets Alex flip between the locked preview and the full (bought) hub
  // without SQL time-travel. Real users never see the switcher.
  const [adminView, setAdminView] = useState(null) // null = follow real state | 'locked' | 'full'
  const isComplete = profile?.program_phase === 'complete'
  const showFull = adminView ? adminView === 'full' : isComplete
  return (
    <>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', margin: '10px auto 4px', maxWidth: 400 }}>
          <span className="sn-micro sn-micro-dim">Admin preview</span>
          {[['Real state', null], ['Locked', 'locked'], ['Unlocked', 'full']].map(([label, val]) => (
            <button key={label} onClick={() => setAdminView(val)}
              style={{ border: 'none', borderRadius: 999, padding: '5px 11px', fontSize: 11, cursor: 'pointer',
                background: adminView === val ? '#22301F' : '#EDEBE4', color: adminView === val ? '#FAF8F4' : '#7A7A72', fontWeight: adminView === val ? 600 : 400 }}>{label}</button>
          ))}
        </div>
      )}
      {showFull ? <MaintainHubInner session={session} profile={profile} /> : <MaintainPreview profile={profile} session={session} />}
    </>
  )
}

function MaintainHubInner({ session, profile }) {

  const [view, setView] = useState('hub') // hub | ask | symptom | history
  const [foodMap, setFoodMap] = useState([])
  const [meals, setMeals] = useState([])
  const [symptoms, setSymptoms] = useState([])
  const [loading, setLoading] = useState(true)

  const load = async () => {
    try {
      const [fm, rr, ml, sl] = await Promise.all([
        supabase.from('food_map').select('*').eq('user_id', session.user.id),
        supabase.from('reintroduction_results').select('food, verdict, started_at').eq('user_id', session.user.id).not('verdict', 'is', null).order('started_at', { ascending: true }),
        supabase.from('meal_logs').select('*').eq('user_id', session.user.id).order('logged_at', { ascending: false }).limit(50),
        supabase.from('symptom_logs').select('*').eq('user_id', session.user.id).order('logged_at', { ascending: false }).limit(50),
      ])
      // The map subscribers pay for reads the UNION (QA 2026-10-29 #3):
      // reintroduction_results is the source of truth, legacy food_map the
      // best-effort mirror — a silently failed upsert must never blank this.
      const byFood = {}
      for (const row of (fm.data || [])) if (row.verdict) byFood[(row.food || '').toLowerCase()] = { food: row.food, verdict: row.verdict }
      for (const cy of (rr.data || [])) byFood[(cy.food || '').toLowerCase()] = { food: cy.food, verdict: cy.verdict }  // ascending: latest trial wins
      setFoodMap(Object.values(byFood))
      setMeals(ml.data || [])
      setSymptoms(sl.data || [])
    } catch (e) {}
    setLoading(false)
  }

  useEffect(() => { load() }, [session.user.id])

  const firstName = profile?.full_name?.split(' ')[0] || 'there'
  const safe = foodMap.filter(f => f.verdict === 'Safe')
  const limit = foodMap.filter(f => f.verdict === 'Limit')
  const avoid = foodMap.filter(f => f.verdict === 'Avoid')

  if (view === 'ask') {
    return (
      <div style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
        <BackBar label="Ask Sensify" onBack={() => setView('hub')} />
        <div style={{ flex: 1, overflow: 'hidden' }}><AskSensify session={session} /></div>
      </div>
    )
  }

  if (view === 'symptom') {
    return <SymptomReport session={session} onBack={() => { setView('hub'); load() }} />
  }

  if (view === 'history') {
    return <HistoryLog meals={meals} symptoms={symptoms} onBack={() => setView('hub')} />
  }

  return (
    <div style={s.wrap}>
      <style>{css}</style>
      <div style={s.body}>
        <div className="sn-rise" style={s.greet}>Hi, <em style={s.em}>{firstName}.</em></div>
        <div className="sn-rise" style={s.subgreet}>{foodMap.length > 0 ? "Your Food Map is loaded. Here's your kit for today." : "Here's your kit for today."}</div>

        {/* Ask Sensify hero */}
        <div className="sn-hero sn-card-hov sn-rise-2" style={s.hero} onClick={() => setView('ask')}>
          <div className="sn-micro sn-micro-dark" style={{ display: 'block', marginBottom: 8 }}>Ask Sensify</div>
          <div style={s.heroTitle}>What's on the menu <em style={{ fontStyle: 'italic', color: '#8BAE8A' }}>today?</em></div>
          <div style={s.heroDesc}>Check any food, read a menu, or just tell me what you ate. I know your whole map.</div>
          <div style={s.heroInput}>
            <span>Ask about a food, or log a meal...</span>
            <span style={s.heroSend}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></span>
          </div>
        </div>

        {/* Food Map summary */}
        <div className="sn-rise-3" style={s.sectionLabel}>Your Food Map</div>
        <div className="sn-rise-3" style={s.mapCard}>
          {loading ? <div style={s.muted}>Loading…</div> : foodMap.length === 0 ? (
            <div style={s.muted}>Your verdicts will appear here as you complete reintroductions.</div>
          ) : (
            <>
              <MapRow tier="Avoid" foods={avoid} />
              <MapRow tier="Limit" foods={limit} />
              <MapRow tier="Safe" foods={safe} />
            </>
          )}
        </div>

        {/* Track tiles */}
        <div className="sn-rise-4" style={s.sectionLabel}>Track</div>
        <div className="sn-rise-4" style={s.grid2}>
          <div className="sn-card-hov" style={s.tile} onClick={() => setView('symptom')}>
            <div style={s.tileIco}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/><circle cx="12" cy="12" r="3"/></svg></div>
            <div style={s.tileT}>Report a symptom</div>
            <div style={s.tileD}>Felt off? Note it so we can spot patterns.</div>
          </div>
          <div className="sn-card-hov" style={s.tile} onClick={() => setView('history')}>
            <div style={s.tileIco}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg></div>
            <div style={s.tileT}>History &amp; trends</div>
            <div style={s.tileD}>Your meals and symptoms over time.</div>
          </div>
        </div>

        {/* The referral card is gone on purpose: it promised 10% off and a
            free month with no referral system behind it and a button that
            did nothing. It returns when referrals are real. */}

        {/* Recent activity */}
        <div className="sn-rise-4" style={s.sectionLabel}>Recent activity</div>
        <div className="sn-rise-4" style={s.activity}>
          {[...meals.slice(0, 2).map(m => ({ type: 'meal', m, at: m.logged_at })),
            ...symptoms.slice(0, 1).map(sm => ({ type: 'symptom', sm, at: sm.logged_at }))]
            .sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 4).map((item, i) => (
            <ActivityRow key={i} item={item} />
          ))}
          {meals.length === 0 && symptoms.length === 0 && <div style={{ ...s.muted, padding: '12px 0' }}>Nothing logged yet. Try asking Sensify about a meal.</div>}
        </div>

        {/* Household link stays manageable after completion (QA 2026-10-29 #5:
            it only rendered mid-protocol, stranding live links). */}
        <div className="sn-rise-4" style={{ marginTop: 14 }}>
          <HouseholdShareCard session={session} profile={profile} />
        </div>
      </div>
    </div>
  )
}

function MapRow({ tier, foods }) {
  if (foods.length === 0) return null
  const t = TIER[tier]
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '7px' }}>
        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: t.dot }} />
        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.17em', color: t.text }}>{tier}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {foods.map((f, i) => <span key={i} style={{ fontSize: '12.5px', fontWeight: 500, padding: '5px 12px', borderRadius: '999px', background: t.bg, color: t.text }}>{f.food}</span>)}
      </div>
    </div>
  )
}

function ActivityRow({ item }) {
  if (item.type === 'meal') {
    const foods = (item.m.foods || []).map(f => f.name).join(', ')
    const flagged = item.m.flagged
    return (
      <div className="sn-row" style={s.actRow}>
        <span style={{ ...s.actDot, background: flagged ? '#E8941F' : '#2C9D8A' }} />
        <span style={s.actTxt}><b>{flagged ? 'Logged (flagged):' : 'Logged:'}</b> {foods || item.m.raw_text}</span>
        <span style={s.actTime}>{fmtTime(item.at)}</span>
      </div>
    )
  }
  return (
    <div className="sn-row" style={s.actRow}>
      <span style={{ ...s.actDot, background: '#A8A69E' }} />
      <span style={s.actTxt}><b>Symptom:</b> {item.sm.severity} {item.sm.symptom.toLowerCase()}</span>
      <span style={s.actTime}>{fmtTime(item.at)}</span>
    </div>
  )
}

// ── Symptom reporting view ──
function SymptomReport({ session, onBack }) {
  const [symptom, setSymptom] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  const [saveErr, setSaveErr] = useState('')
  const save = async () => {
    if (!symptom || !severity) return
    setSaving(true)
    setSaveErr('')
    // Checked write (QA 2026-10-29 #6, 10th silent-write sighting): symptom
    // logs are drift detection's only signal — a lost one must never read as
    // "Logged. Thanks."
    const { error } = await supabase.from('symptom_logs').insert({ user_id: session.user.id, symptom, severity, note: note || null })
    if (error) {
      setSaveErr("That didn't save — your note is still here, tap Log it to try again.")
      setSaving(false)
      return
    }
    setDone(true)
    setTimeout(onBack, 1100)
  }

  if (done) return (
    <div style={s.wrap}><div style={{ ...s.body, textAlign: 'center', paddingTop: '80px' }}>
      <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#EDF3ED', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D5C3C" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: '20px', fontWeight: 300 }}>Logged. Thanks for noting it.</div>
    </div></div>
  )

  return (
    <div style={s.wrap}>
      <style>{css}</style>
      <BackBar label="Report a symptom" onBack={onBack} />
      <div style={s.body}>
        <div style={s.qLabel}>What are you feeling?</div>
        <div style={s.chipWrap}>
          {SYMPTOM_OPTIONS.map(opt => (
            <button key={opt} className={symptom === opt ? 'sn-chip sn-chip-on' : 'sn-chip'} onClick={() => setSymptom(opt)}>{opt}</button>
          ))}
        </div>

        <div style={{ ...s.qLabel, marginTop: '24px' }}>How intense?</div>
        <div style={s.sevRow}>
          {SEVERITY.map(sv => (
            <button key={sv} className={severity === sv ? 'sn-chip sn-chip-on' : 'sn-chip'} style={{ flex: 1, justifyContent: 'center', textTransform: 'capitalize' }} onClick={() => setSeverity(sv)}>{sv}</button>
          ))}
        </div>

        <div style={{ ...s.qLabel, marginTop: '24px' }}>Anything to add? <span style={{ fontWeight: 400, color: '#A8A69E' }}>(optional)</span></div>
        <textarea style={s.textarea} rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="" />

        {saveErr && <div style={{ background: '#FAEAEA', color: '#8B2E2E', borderRadius: 12, padding: '10px 13px', fontSize: 12.5, lineHeight: 1.55, marginTop: 16 }}>{saveErr}</div>}
        <button className="sn-btn" style={{ width: '100%', marginTop: saveErr ? '10px' : '24px' }} disabled={!symptom || !severity || saving} onClick={save}>
          {saving ? 'Saving…' : 'Log symptom'}
        </button>
        <div style={s.disclaimer}>For severe or concerning symptoms, contact a healthcare provider. This log helps spot patterns, it is not medical care.</div>
      </div>
    </div>
  )
}

// ── History & trends view ──
function HistoryLog({ meals, symptoms, onBack }) {
  const [tab, setTab] = useState('meals')
  return (
    <div style={s.wrap}>
      <style>{css}</style>
      <BackBar label="History & trends" onBack={onBack} />
      <div style={s.body}>
        <div style={s.histTabs}>
          <button className={tab === 'meals' ? 'sn-chip sn-chip-on' : 'sn-chip'} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setTab('meals')}>Meals</button>
          <button className={tab === 'symptoms' ? 'sn-chip sn-chip-on' : 'sn-chip'} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setTab('symptoms')}>Symptoms</button>
        </div>

        {tab === 'meals' ? (
          meals.length === 0 ? <div style={s.muted}>No meals logged yet.</div> :
          groupByDate(meals).map(([date, items]) => (
            <div key={date} style={{ marginBottom: '18px' }}>
              <div style={s.histDate}>{fmtDate(date)}</div>
              {items.map((m, i) => (
                <div key={i} className="sn-row" style={s.histRow}>
                  <span style={{ ...s.actDot, background: m.flagged ? '#E8941F' : '#2C9D8A', marginTop: '6px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={s.histFoods}>{(m.foods || []).map(f => f.name).join(', ') || m.raw_text}</div>
                    {m.flagged && <div style={s.histFlag}>{(m.foods || []).filter(f => f.mapStatus === 'avoid' || f.mapStatus === 'limit').map(f => `${f.name} (${f.mapStatus})`).join(', ')}</div>}
                  </div>
                  <span style={s.actTime}>{fmtTime(m.logged_at)}</span>
                </div>
              ))}
            </div>
          ))
        ) : (
          symptoms.length === 0 ? <div style={s.muted}>No symptoms logged yet.</div> :
          groupByDate(symptoms).map(([date, items]) => (
            <div key={date} style={{ marginBottom: '18px' }}>
              <div style={s.histDate}>{fmtDate(date)}</div>
              {items.map((sm, i) => (
                <div key={i} className="sn-row" style={s.histRow}>
                  <span style={{ ...s.actDot, background: sm.severity === 'severe' ? '#D64545' : sm.severity === 'moderate' ? '#E8941F' : '#A8A69E', marginTop: '6px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={s.histFoods}>{sm.symptom} <span style={{ color: '#A8A69E', fontSize: '12px' }}>· {sm.severity}</span></div>
                    {sm.note && <div style={s.histFlag}>{sm.note}</div>}
                  </div>
                  <span style={s.actTime}>{fmtTime(sm.logged_at)}</span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function BackBar({ label, onBack }) {
  return (
    <div style={s.backBar}>
      <button style={s.backBtn} onClick={onBack}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1C1C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>
      <span style={s.backLabel}>{label}</span>
    </div>
  )
}

// ── helpers ──
function fmtTime(d) {
  const date = new Date(d)
  const now = new Date()
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase()
  const yest = new Date(now); yest.setDate(now.getDate() - 1)
  if (date.toDateString() === yest.toDateString()) return 'Yesterday'
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
function fmtDate(dateStr) {
  const date = new Date(dateStr + 'T12:00:00')
  const now = new Date()
  if (date.toDateString() === now.toDateString()) return 'Today'
  const yest = new Date(now); yest.setDate(now.getDate() - 1)
  if (date.toDateString() === yest.toDateString()) return 'Yesterday'
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}
function groupByDate(items) {
  const groups = {}
  // Timestamps group by LOCAL date (QA 2026-10-29 minor: splitting the ISO
  // string grouped late-evening US logs under tomorrow's UTC header).
  const localDay = (iso) => {
    const d = new Date(iso)
    if (isNaN(d)) return String(iso || '').split('T')[0]
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  for (const it of items) {
    const d = it.log_date || localDay(it.logged_at)
    if (!groups[d]) groups[d] = []
    groups[d].push(it)
  }
  return Object.entries(groups).sort((a, b) => new Date(b[0]) - new Date(a[0]))
}

const css = `@keyframes none {}`

const s = {
  wrap: { maxWidth: '680px', margin: '0 auto', width: '100%', minHeight: '100%' },
  body: { padding: '22px 18px 40px' },
  greet: { fontFamily: 'Fraunces, serif', fontSize: '28px', fontWeight: 300 },
  em: { fontStyle: 'italic', color: '#3D5C3C' },
  subgreet: { fontSize: '13px', color: '#7A7A72', marginTop: '3px', marginBottom: '22px' },
  sectionLabel: { fontFamily: 'DM Mono, monospace', fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.17em', color: 'var(--forest-500)', margin: '24px 0 11px' },
  muted: { fontSize: '13px', color: '#A8A69E', lineHeight: 1.5 },

  hero: { padding: '22px 20px', cursor: 'pointer' },
  heroTitle: { fontFamily: 'Fraunces, serif', fontSize: '22px', fontWeight: 300, color: 'white', marginBottom: '6px' },
  heroDesc: { fontSize: '12.5px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, marginBottom: '14px' },
  heroInput: { background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '999px', padding: '11px 11px 11px 16px', fontSize: '12px', color: 'rgba(255,255,255,0.55)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  heroSend: { width: '26px', height: '26px', borderRadius: '999px', background: '#3D5C3C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },

  mapCard: { background: 'var(--paper)', border: 'none', borderRadius: '24px', boxShadow: 'var(--shadow-2)', padding: '18px' },

  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  tile: { background: 'var(--paper)', border: 'none', borderRadius: '16px', boxShadow: 'var(--shadow-1)', padding: '15px', cursor: 'pointer' },
  tileIco: { width: '32px', height: '32px', borderRadius: '10px', background: '#EDF3ED', color: '#3D5C3C', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' },
  tileT: { fontSize: '13px', fontWeight: 600, marginBottom: '3px' },
  tileD: { fontSize: '11px', color: '#7A7A72', lineHeight: 1.4 },


  activity: { background: 'var(--paper)', border: 'none', borderRadius: '24px', boxShadow: 'var(--shadow-2)', padding: '12px' },
  actRow: { marginBottom: '8px' },
  actDot: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  actTxt: { fontSize: '12px', flex: 1, lineHeight: 1.4 },
  actTime: { fontSize: '10px', color: '#A8A69E', fontFamily: 'DM Mono, monospace', flexShrink: 0 },

  backBar: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px' },
  backBtn: { width: '32px', height: '32px', borderRadius: '10px', border: 'none', background: '#F1EFE8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-1)' },
  backLabel: { fontSize: '15px', fontWeight: 600 },

  qLabel: { fontSize: '14px', fontWeight: 600, marginBottom: '11px' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  sevRow: { display: 'flex', gap: '8px' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '13px', borderRadius: '16px', border: 'none', boxShadow: 'var(--shadow-1)', background: 'var(--paper)', fontSize: '16px', resize: 'vertical', lineHeight: 1.5 },
  disclaimer: { fontSize: '11px', color: '#A8A69E', textAlign: 'center', marginTop: '14px', lineHeight: 1.5 },

  histTabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  histDate: { fontFamily: 'DM Mono, monospace', fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.17em', color: 'var(--forest-500)', marginBottom: '9px' },
  histRow: { alignItems: 'flex-start', marginBottom: '8px' },
  histFoods: { fontSize: '13.5px', lineHeight: 1.4 },
  histFlag: { fontSize: '11px', color: '#8A5410', marginTop: '3px' },
}
